import React, { useState, useEffect } from 'react';
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Table,
  message,
  Modal,
  Input,
  Typography,
  Row,
  Col,
  Statistic
} from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import apiClient from '../services/api';
import { AssetTransfer, AssetTransferItem } from '../types/assetTransfer';
import * as XLSX from 'xlsx';

const { Title, Text } = Typography;


const AssetTransferDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [transfer, setTransfer] = useState<AssetTransfer | null>(null);
  const [transferItems, setTransferItems] = useState<AssetTransferItem[]>([]);

  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [approveDocumentCode, setApproveDocumentCode] = useState('');
  const [approveLoading, setApproveLoading] = useState(false);

  // Hàm xuất Excel
  const handleExportExcel = () => {
    if (!transfer || transferItems.length === 0) {
      message.warning('Không có dữ liệu để xuất Excel');
      return;
    }

    try {
      // Chuẩn bị dữ liệu cho Excel
      const excelData = transferItems.map((item, index) => ({
        'STT': index + 1,
        'Mã tài sản': item.asset_code || '',
        'Tên tài sản': item.asset_name || '',
        'Danh mục': item.category_name || '',
        'Vị trí': item.location_name || '',
        'Trạng thái': item.asset_status ? getAssetStatusText(item.asset_status) : '',
        'Tình trạng kỹ thuật': item.technical_condition ? getTechnicalConditionText(item.technical_condition) : ''
      }));

      // Tạo workbook và worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Danh sách tài sản điều chuyển');

      // Điều chỉnh độ rộng cột
      const colWidths = [
        { wch: 5 },   // STT
        { wch: 15 },  // Mã tài sản
        { wch: 30 },  // Tên tài sản
        { wch: 25 },  // Danh mục
        { wch: 20 },  // Vị trí
        { wch: 15 },  // Trạng thái
        { wch: 20 }   // Tình trạng kỹ thuật
      ];
      ws['!cols'] = colWidths;

      // Tạo tên file
      const fileName = `Phieu_dieu_chuyen_${transfer.code}_${dayjs().format('DDMMYYYY_HHmmss')}.xlsx`;

      // Xuất file
      XLSX.writeFile(wb, fileName);
      message.success('Xuất Excel thành công!');
    } catch (error) {
      message.error('Có lỗi xảy ra khi xuất Excel');
    }
  };

  // Hàm helper để chuyển đổi trạng thái tài sản
  const getAssetStatusText = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'in_use': 'Đang sử dụng',
      'in_storage': 'Lưu kho',
      'under_maintenance': 'Đang bảo dưỡng',
      'disposed': 'Đã thanh lý',
      'lost': 'Mất',
      'damaged': 'Hỏng',
      'pending_deployment': 'Chờ triển khai'
    };
    return statusMap[status] || status;
  };

  // Hàm helper để chuyển đổi tình trạng kỹ thuật
  const getTechnicalConditionText = (condition: string) => {
    const conditionMap: { [key: string]: string } = {
      'new': 'Mới',
      'good': 'Tốt',
      'fair': 'Khá',
      'poor': 'Kém',
      'bad': 'Hỏng',
      'average': 'Trung bình'
    };
    return conditionMap[condition] || condition;
  };

  const loadTransferDetails = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await apiClient.getAssetTransferById(parseInt(id)) as any;
      setTransfer(response.data);
      setTransferItems(response.data.transfer_items || []); // Assuming transfer_items is part of the response
    } catch (error: any) {
      message.error('Lỗi khi tải chi tiết phiếu điều chuyển: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransferDetails();
  }, [id]); // loadTransferDetails will be stable after first render

  const handleApprove = async () => {
    if (!approveDocumentCode.trim()) {
      message.error('Vui lòng nhập hồ sơ bàn sao từ văn phòng điện tử');
      return;
    }

    setApproveLoading(true);
    try {
      await apiClient.approveAssetTransfer(parseInt(id!), {
        approval_document_code: approveDocumentCode.trim()
      });
      message.success('Duyệt phiếu điều chuyển thành công');
      setApproveModalVisible(false);
      setApproveDocumentCode('');
      loadTransferDetails();
    } catch (error: any) {
      message.error('Lỗi khi duyệt phiếu điều chuyển: ' + error.message);
    } finally {
      setApproveLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await apiClient.deleteAssetTransfer(parseInt(id!));
      message.success('Xóa phiếu điều chuyển thành công');
      navigate('/asset-transfers');
    } catch (error: any) {
      message.error('Lỗi khi xóa phiếu điều chuyển: ' + error.message);
    }
  };



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'approved': return 'green';
      case 'cancelled': return 'red';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Chờ duyệt';
      case 'approved': return 'Đã duyệt (Hoàn thành)';
      case 'cancelled': return 'Đã hủy';
      default: return status;
    }
  };

  const assetColumns = [
    {
      title: 'Mã tài sản',
      dataIndex: 'asset_code',
      key: 'asset_code',
      ellipsis: true,
    },
    {
      title: 'Tên tài sản',
      dataIndex: 'asset_name',
      key: 'asset_name',
      ellipsis: true,
    },
    {
      title: 'Danh mục',
      dataIndex: 'category_name',
      key: 'category_name',
      ellipsis: true,
    },
    {
      title: 'Vị trí',
      dataIndex: 'location_name',
      key: 'location_name',
      ellipsis: true,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'asset_status',
      key: 'asset_status',
      ellipsis: true,
      render: (status: string) => {
        const statusMap: { [key: string]: string } = {
          'in_use': 'Đang sử dụng',
          'in_storage': 'Lưu kho',
          'under_maintenance': 'Đang bảo dưỡng',
          'disposed': 'Đã thanh lý',
          'lost': 'Mất',
          'damaged': 'Hỏng',
          'pending_deployment': 'Chờ triển khai'
        };
        return statusMap[status] || status;
      },
    },
    {
      title: 'Tình trạng',
      dataIndex: 'technical_condition',
      key: 'technical_condition',
      ellipsis: true,
      render: (condition: string) => {
        const conditionMap: { [key: string]: string } = {
          'new': 'Mới',
          'good': 'Tốt',
          'fair': 'Khá',
          'poor': 'Kém',
          'bad': 'Hỏng',
          'average': 'Trung bình'
        };
        return conditionMap[condition] || condition;
      },
    },
  ];

  if (loading) {
    return <div style={{ padding: 24, textAlign: 'center' }}>Đang tải...</div>;
  }

  if (!transfer) {
    return <div style={{ padding: 24, textAlign: 'center' }}>Không tìm thấy phiếu điều chuyển</div>;
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/asset-transfers')}
          style={{ padding: 4, height: 'auto' }}
        />
        <Title level={2} style={{ margin: 0 }}>
          Chi tiết phiếu điều chuyển: {transfer.code}
        </Title>
      </div>

      <Row gutter={24} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Trạng thái"
              value={getStatusText(transfer.status)}
              valueStyle={{ color: getStatusColor(transfer.status) === 'green' ? '#52c41a' : '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Số lượng tài sản"
              value={transfer.asset_count || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Ngày tạo"
              value={dayjs(transfer.created_at).format('DD/MM/YYYY')}
              valueStyle={{ color: '#666' }}
            />
          </Card>
        </Col>
      </Row>

      <Card title="Thông tin phiếu điều chuyển" style={{ marginBottom: 24 }}>
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Mã phiếu">{transfer.code}</Descriptions.Item>
          <Descriptions.Item label="Trạng thái">
            <Tag color={getStatusColor(transfer.status)}>
              {getStatusText(transfer.status)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Người bàn giao">
            {transfer.from_employee_name}{transfer.from_employee_code ? ` (${transfer.from_employee_code})` : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Người nhận">
            {transfer.to_employee_name}{transfer.to_employee_code ? ` (${transfer.to_employee_code})` : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Lý do điều chuyển" span={2}>
            {transfer.reason || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Người tạo">{transfer.created_by_username}</Descriptions.Item>
          <Descriptions.Item label="Ngày tạo">
            {dayjs(transfer.created_at).format('DD/MM/YYYY HH:mm')}
          </Descriptions.Item>
          {transfer.approved_by_username && (
            <>
              <Descriptions.Item label="Người duyệt">{transfer.approved_by_username}</Descriptions.Item>
              <Descriptions.Item label="Ngày duyệt">
                {transfer.approval_date ? dayjs(transfer.approval_date).format('DD/MM/YYYY HH:mm') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Hồ sơ duyệt" span={2}>
                {transfer.approval_document_code || '—'}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      </Card>

      <Card
        title="Danh sách tài sản điều chuyển"
        extra={
          <Space>
            {transfer.status === 'pending' && (
              <>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => setApproveModalVisible(true)}
                >
                  Duyệt phiếu
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDelete}
                >
                  Xóa phiếu
                </Button>
              </>
            )}

            <Button icon={<FileExcelOutlined />} onClick={handleExportExcel}>
              Xuất Excel
            </Button>
          </Space>
        }
      >
        <Table
          columns={assetColumns}
          dataSource={transferItems}
          rowKey="id"
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title="Duyệt phiếu điều chuyển"
        open={approveModalVisible}
        onOk={handleApprove}
        onCancel={() => {
          setApproveModalVisible(false);
          setApproveDocumentCode('');
        }}
        confirmLoading={approveLoading}
        okText="Duyệt"
        cancelText="Hủy"
      >
        <div style={{ marginBottom: 16 }}>
          <Text>Vui lòng nhập hồ sơ bàn sao từ văn phòng điện tử:</Text>
        </div>
        <Input
          placeholder="Nhập mã hồ sơ..."
          value={approveDocumentCode}
          onChange={(e) => setApproveDocumentCode(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default AssetTransferDetailPage; 